import { firstNameSegment } from '../../shared/utils/first-name-segment.util';
import { normalizeListSearch } from '../../shared/utils/normalize-list-search.util';
import { BaseService } from '../base';
import type {
  CreateInspectionResult,
  DecideInspectionPayload,
  GetInspectionsParams,
  GetInspectionsSummaryParams,
  InspectionApiItem,
  InspectionHistoryApiResponse,
  InspectionRecordsApiResponse,
  InspectionsApiResponse,
  InspectionsResponse,
  InspectionsSummaryResponse,
  InspectionStatus,
  InspectionSummary,
  PolicyInspectionsApiResponse,
  SaveTechnicalOpinionPayload,
} from './types';

const DEFAULT_PAGE_SIZE = 10;
const PORTAL_API_URL = import.meta.env.VITE_API_URL as string | undefined;

function mapApiItemToSummary(item: InspectionApiItem): InspectionSummary {
  const id = item.policyId || item.id || '';
  const inspectionId = item.policyInspection?.id?.trim() ?? '';
  const createdAt = item.proposalDate || '';

  const statusPortal = item.statusPortal ?? item.proposalStatus ?? '';
  const proposalStatus =
    typeof statusPortal === 'string'
      ? statusPortal.trim()
      : String(statusPortal);

  // Quando `statusInspection` não está preenchido (ex.: produtos empresariais
  // ainda em triagem, sem PolicyStatusInspection definido pelo InsureMO),
  // mantemos vazio para que a tabela exiba "Em Triagem". Antes caía no
  // `proposalStatus` (um número), que não casava com nenhum InspectionStatus
  // e a UI mostrava "Desconhecido".
  const inspectionStatus = item.statusInspection
    ? item.statusInspection.trim()
    : '';

  return {
    id,
    inspectionId,
    proposerName: firstNameSegment(item.insuredName) || '-',
    proposerDocument: firstNameSegment(item.registerNumber) || '',
    brokerName: item.brokerName || '-',
    brokerDocument: item.brokerRegisterNumber || '',
    inclusionUserName:
      item.inclusionUserName?.trim() || item.insertByUserName?.trim() || '-',
    product: item.productName || '-',
    proposalNumber:
      item.proposalNumber != null ? String(item.proposalNumber) : '-',
    createdAt,
    transmissionDate: item.transmissionDate || createdAt,
    proposalStatus,
    inspectionStatus,
  };
}

export class InspectionsService extends BaseService {
  constructor() {
    super();
  }

  async getInspections(params: GetInspectionsParams = {}): Promise<{
    success: boolean;
    data?: InspectionsResponse;
    error?: string;
  }> {
    const {
      pageNumber = 1,
      pageSize = DEFAULT_PAGE_SIZE,
      search = '',
      sortField = 'index_time',
      sortType = 'asc',
      status = '',
      inspectionStatus = '',
      statusPortal,
      partyCode,
      partyId,
      advisoryId,
      responsibleId,
      productCode,
      proposalDateFrom,
      proposalDateTo,
    } = params;
    const normalizedSearch = normalizeListSearch(search);

    try {
      const queryParams = new URLSearchParams({
        pageNumber: String(pageNumber),
        pageSize: String(pageSize),
        sortField,
        sortType,
      });

      if (normalizedSearch) queryParams.append('filter', normalizedSearch);
      if (status) queryParams.append('status', status);
      if (inspectionStatus)
        queryParams.append('inspectionStatus', inspectionStatus);
      if (statusPortal != null && !Number.isNaN(statusPortal)) {
        queryParams.append('statusPortal', String(statusPortal));
      }
      if (partyCode) queryParams.append('partyCode', partyCode);
      if (partyId) queryParams.append('partyId', partyId);
      if (advisoryId) queryParams.append('advisoryId', advisoryId);
      if (responsibleId) queryParams.append('responsibleId', responsibleId);
      if (productCode) queryParams.append('productCode', productCode);
      if (proposalDateFrom)
        queryParams.append('proposalDateFrom', proposalDateFrom);
      if (proposalDateTo) queryParams.append('proposalDateTo', proposalDateTo);

      const path = `/portal/v1/policies/inspections?${queryParams.toString()}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      const json =
        await this.handleJsonResponse<InspectionsApiResponse>(response);

      if (!json.success || !json.data) {
        return {
          success: false,
          error: json.error || 'Erro ao carregar inspeções',
        };
      }

      return {
        success: true,
        data: {
          data: (json.data.data || []).map(mapApiItemToSummary),
          meta: json.data.meta,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async getInspectionsByPolicyId(policyId: string): Promise<{
    success: boolean;
    data?: PolicyInspectionsApiResponse;
    error?: string;
  }> {
    const sanitizedPolicyId = policyId?.trim();

    if (!sanitizedPolicyId) {
      return { success: false, error: 'PolicyId inválido' };
    }

    try {
      const path = `/portal/v1/policies/${encodeURIComponent(sanitizedPolicyId)}/inspections`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      const json =
        await this.handleJsonResponse<PolicyInspectionsApiResponse>(response);

      if (!json.success) {
        return {
          success: false,
          error: json.error || 'Erro ao carregar inspeções',
        };
      }

      // O endpoint agora retorna um array direto
      const data = Array.isArray(json.data) ? json.data : [];

      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async getInspectionsRecordsByPolicyId(
    policyId: string | null | undefined,
    inspectionId: string | null | undefined
  ): Promise<{
    success: boolean;
    data?: InspectionRecordsApiResponse;
    error?: string;
  }> {
    const sanitizedPolicyId = policyId?.trim();
    const sanitizedInspectionId = inspectionId?.trim();

    if (!sanitizedPolicyId || !sanitizedInspectionId) {
      return { success: false, error: 'PolicyId inválido' };
    }

    try {
      const path = `/portal/v1/policies/${encodeURIComponent(sanitizedPolicyId)}/inspections/${encodeURIComponent(sanitizedInspectionId)}/records`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      const json =
        await this.handleJsonResponse<InspectionRecordsApiResponse>(response);

      if (!json.success) {
        return {
          success: false,
          error: json.error || 'Erro ao carregar registros de interações',
        };
      }

      // O endpoint agora retorna um array direto
      const data = Array.isArray(json.data) ? json.data : [];

      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async getInspectionStatuses(): Promise<{
    success: boolean;
    data?: InspectionStatus[];
    error?: string;
  }> {
    try {
      const path = '/portal/v1/policies/inspections/status';
      const response = await this.get(path, {}, PORTAL_API_URL);
      const json = await this.handleJsonResponse<InspectionStatus[]>(response);

      if (!json.success) {
        return {
          success: false,
          error: json.error || 'Erro ao carregar status de inspeção',
        };
      }

      const data = Array.isArray(json.data) ? json.data : [];

      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async decideInspection(
    policyId: string,
    inspectionId: string,
    body: DecideInspectionPayload
  ): Promise<{ success: boolean; data?: unknown; error?: string }> {
    try {
      const path = `/portal/v1/policies/${encodeURIComponent(policyId)}/inspections/${encodeURIComponent(inspectionId)}/decision`;
      const response = await this.post(path, body, PORTAL_API_URL);
      return this.handleJsonResponse(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async saveTechnicalOpinion(
    policyId: string,
    inspectionId: string,
    body: SaveTechnicalOpinionPayload
  ): Promise<{ success: boolean; data?: unknown; error?: string }> {
    try {
      const path = `/portal/v1/policies/${encodeURIComponent(policyId)}/inspections/${encodeURIComponent(inspectionId)}/follow-up`;
      const response = await this.post(path, body, PORTAL_API_URL);
      return this.handleJsonResponse(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async getInspectionHistory(policyId: string): Promise<{
    success: boolean;
    data?: InspectionHistoryApiResponse;
    error?: string;
  }> {
    try {
      const path = `/portal/v1/policies/${encodeURIComponent(policyId)}/inspections/history`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      const json =
        await this.handleJsonResponse<InspectionHistoryApiResponse>(response);

      if (!json.success) {
        return {
          success: false,
          error: json.error || 'Erro ao carregar histórico de inspeções',
        };
      }

      const data = Array.isArray(json.data) ? json.data : [];
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async getInspectionsSummary(
    params: GetInspectionsSummaryParams = {}
  ): Promise<{
    success: boolean;
    data?: InspectionsSummaryResponse;
    error?: string;
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (params.productCode)
        queryParams.append('productCode', params.productCode);
      if (params.proposalDateFrom)
        queryParams.append('proposalDateFrom', params.proposalDateFrom);
      if (params.proposalDateTo)
        queryParams.append('proposalDateTo', params.proposalDateTo);
      if (params.partyCode) queryParams.append('partyCode', params.partyCode);

      const query = queryParams.toString();
      const path = `/portal/v1/policies/inspections/summary${query ? `?${query}` : ''}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      const json =
        await this.handleJsonResponse<InspectionsSummaryResponse>(response);

      if (!json.success || !json.data) {
        return {
          success: false,
          error: json.error || 'Erro ao carregar resumo de inspeções',
        };
      }

      return { success: true, data: json.data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async createInspection(policyId: string): Promise<{
    success: boolean;
    data?: CreateInspectionResult;
    error?: string;
  }> {
    try {
      const path = `/portal/v1/policies/${encodeURIComponent(policyId)}/inspections`;
      const response = await this.post(path, {}, PORTAL_API_URL);
      return this.handleJsonResponse<CreateInspectionResult>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async getInspectionDocument(
    policyId: string,
    type: string,
  ): Promise<{
    success: boolean;
    data?: { key: string; url: string };
    error?: string;
  }> {
    try {
      const path = `/portal/v1/policies/${encodeURIComponent(policyId)}/inspections/documents?type=${encodeURIComponent(type)}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return this.handleJsonResponse<{ key: string; url: string }>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao buscar documento de inspeção',
      };
    }
  }
}

export const inspectionsService = new InspectionsService();
